<template>
  <div class="waw-connect">
    <p class="waw-connect-intro">
      Click <strong>Connect WhatsApp</strong> and a Facebook window opens. Keep this screen beside
      it &mdash; you will need it in the Facebook window. When you finish, come back: we take over
      and set up the rest.
    </p>

    <WhatsAppSignInSteps :phone="whatsappNumber" :display-name="displayName" />

    <div v-if="facebookAccess === 'other'" class="waw-note">
      <p class="waw-note__text">
        You told us someone outside the practice looks after your Facebook account. WhatsApp requires
        this sign-in to come from whoever holds that login, so they need to be the one to do it
        &mdash; either sitting here with you, signed in on this computer, or from a link you send
        them.
      </p>

      <button
        type="button"
        class="waw-linkbtn"
        :disabled="busy"
        data-test="waw-connect-get-link"
        @click="$emit('connect-link')"
      >
        <span v-if="busy"><b-spinner small class="mr-2" />Working&hellip;</span>
        <span v-else>{{ connectLink ? 'Get a new link' : 'Get a link to send them' }}</span>
      </button>

      <div v-if="connectLink" class="waw-link">
        <label class="waw-link__label" for="waw-connect-link-input">
          Send this link to whoever looks after your Facebook account
        </label>
        <div class="waw-link__row">
          <input
            id="waw-connect-link-input"
            ref="linkInput"
            class="waw-link__input"
            :value="connectLink"
            readonly
            data-test="waw-connect-link"
            @focus="$event.target.select()"
          >
          <button
            type="button"
            class="waw-linkbtn"
            data-test="waw-connect-copy-link"
            @click="copyLink"
          >
            {{ copied ? 'Copied' : 'Copy' }}
          </button>
        </div>
        <p class="waw-link__note">
          This link works for {{ ttlLabel }}, so send it while they are around to use it. You can
          get a new one here whenever you need to.
        </p>
        <p v-if="copyFailed" class="waw-link__note waw-link__note--warn" data-test="waw-connect-copy-failed">
          We couldn&rsquo;t copy it for you &mdash; the link is selected above, so copy it from
          there.
        </p>
      </div>
    </div>
    <p v-else-if="facebookAccess === 'none'" class="waw-note">
      You told us the practice doesn&rsquo;t have a Facebook account. That&rsquo;s fine &mdash; the
      sign-in creates what&rsquo;s missing as you go. You&rsquo;ll need a personal Facebook login to
      start it from.
    </p>

    <p v-if="!sdkReady" class="waw-waiting">
      <b-spinner small class="mr-2" />Getting the Facebook sign-in ready&hellip;
    </p>

    <p class="waw-fineprint">
      We never see or store your Facebook password. If you close the window without finishing,
      nothing is lost &mdash; come back to this page and start it again.
    </p>
  </div>
</template>

<script>
/**
 * Wizard screen — the one part of this setup that can never be done for the practice.
 *
 * Meta requires the practice's own Facebook login (WHATSAPP.md: "the customer's Facebook login is
 * always required") and we never handle their credentials. So this screen's whole job is to make a
 * step that FEELS risky feel routine: say what will happen before it happens, say what we can and
 * cannot see, and say that abandoning it costs nothing.
 *
 * ── NO META SDK CODE LIVES HERE ──────────────────────────────────────────────────────────────────
 * The Meta JS SDK loader, the FB.login call, the postMessage listener and the signup-callback POST
 * all stay in WhatsAppTab.vue, which already owned them for the non-wizard flow. This screen only
 * renders the guidance; the frame's primary button emits up to the shell, which emits up to the
 * tab, which runs the connect it has always run. A second loader would mean two `<script>` tags
 * racing for `window.FB` and two listeners answering one postMessage.
 *
 * That the button is disabled until `sdkReady` is deliberate and pre-existing: Meta's SDK is not
 * loaded speculatively, only for the states that can actually use it.
 *
 * ── THE LINK, FOR THE PERSON WHO IS NOT IN THE ROOM ──────────────────────────────────────────────
 * When triage said someone OUTSIDE the practice holds the Facebook login, the sign-in cannot happen
 * on this screen at all — the practice manager sitting here does not have the credentials, and
 * asking them to fetch a password from their marketing agency is exactly the request that ends a
 * setup. So this variant also offers the public `/whatsapp-connect?token=…` page the operator flow
 * has always had, minted by `my-connect-init` and rendered for them to send on.
 *
 * ⚠️ IT IS AN HOUR-LONG LINK, NOT A WEEK-LONG ONE. `my-connect-init` mints with
 * `SELF_START_TOKEN_TTL_MS` (1h); the SEVEN-day link belongs to the operator-only
 * `/wa-onboarding/init`. The screen says so, in words, from the mirrored constant — because a
 * practice who is told "a week" will forward it to their agency and hear back tomorrow that our
 * page is broken. Re-minting is one click, which is the real answer to a short link.
 *
 * The button only ASKS: the shell owns axios, the error banner and the busy flag, exactly as it
 * does for every other request in this wizard.
 *
 * ── WHICH NUMBER TO ENTER (2026-09-11) ───────────────────────────────────────────────────────────
 * Facebook's sign-in asks for a phone number and takes any number. This screen used to say "Follow
 * Facebook's prompts" and never named one — fine for a practice with one number, dangerous for one
 * that keeps its WhatsApp Business app number AND has a Teamio number. The steps, and the number,
 * now come from the SAME component the public link page renders (WhatsAppSignInSteps.vue), so the
 * person here and the person at the other end of the link read identical instructions.
 */
import { CONNECT_LINK_TTL_LABEL } from '../wizardActions';
import WhatsAppSignInSteps from '@/pages/WhatsAppConnect/WhatsAppSignInSteps.vue';

export default {
  name: 'WizardConnectStep',

  components: { WhatsAppSignInSteps },

  props: {
    /** The number to type into Facebook — my-status `whatsappNumber` (the site's GENERAL_PHONE). */
    whatsappNumber: { type: String, default: '' },
    /** The WhatsApp name the practice chose in triage; '' falls back to generic wording. */
    displayName: { type: String, default: '' },
    /** 'self' | 'other' | 'none' from the saved triage answers — drives the extra guidance only. */
    facebookAccess: { type: String, default: '' },
    /** Meta's SDK has loaded; until then the frame's Connect button stays disabled. */
    sdkReady: { type: Boolean, default: false },
    /** The full public URL, built by the shell from a minted token. Empty until one is asked for. */
    connectLink: { type: String, default: '' },
    /** A request is in flight somewhere in the wizard. */
    busy: { type: Boolean, default: false },
  },

  data() {
    return {
      copied: false,
      copyFailed: false,
    };
  },

  computed: {
    ttlLabel() {
      return CONNECT_LINK_TTL_LABEL;
    },
  },

  watch: {
    /** A NEW link has not been copied. Saying "Copied" over a link they have not got is a trap. */
    connectLink() {
      this.copied = false;
      this.copyFailed = false;
    },
  },

  methods: {
    /**
     * Copy the link, and when the browser will not, fall back to selecting it.
     *
     * `navigator.clipboard` needs a secure context and a user gesture, and some browsers refuse it
     * outright. The fallback is not `document.execCommand('copy')` (deprecated, and it fails
     * silently in the same places): it selects the text and says so, which always works and never
     * lies about having copied something.
     */
    async copyLink() {
      this.copyFailed = false;
      try {
        if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('no clipboard');
        await navigator.clipboard.writeText(this.connectLink);
        this.copied = true;
      } catch (e) {
        console.error('WA wizard: copying the connect link failed:', e);
        this.copied = false;
        this.copyFailed = true;
        const input = this.$refs.linkInput;
        if (input && typeof input.select === 'function') {
          input.focus();
          input.select();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../PatientPayments/tokens';

.waw-connect-intro {
  margin: 0 0 1rem;
  color: $pp-text-muted;
  font-size: 0.88rem;
  line-height: 1.7;
  max-width: 640px;

  strong { color: $pp-text-strong; }
}

.waw-note {
  margin: 0 0 1rem;
  padding: 0.65rem 0.85rem;
  border-radius: $pp-radius-sm;
  background: $pp-brand-subtle;
  border: 1px solid $pp-brand-border;
  color: $pp-text-strong;
  font-size: 0.83rem;
  line-height: 1.6;
  max-width: 640px;
}

.waw-note__text {
  margin: 0;
}

.waw-linkbtn {
  display: inline-flex;
  align-items: center;
  margin-top: 0.7rem;
  padding: 0.42rem 0.85rem;
  font-size: 0.83rem;
  font-weight: 700;
  color: $pp-brand;
  background: $pp-surface;
  border: 1px solid $pp-border-strong;
  border-radius: $pp-radius-sm;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    border-color: $pp-brand;
    background: $pp-brand-subtle;
  }

  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
}

.waw-link {
  margin-top: 0.9rem;
}

.waw-link__label {
  display: block;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: $pp-text-subtle;
  margin-bottom: 0.3rem;
}

.waw-link__row {
  display: flex;
  align-items: stretch;
  gap: 0.5rem;

  /* The copy button sits on the row, so it must not carry the standalone button's top margin. */
  .waw-linkbtn {
    margin-top: 0;
    flex: 0 0 auto;
  }
}

/* A plain <input>, not b-form-input: it carries this component's scope attribute directly, so the
   styling cannot leak (repo CSS rule). */
.waw-link__input {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0.42rem 0.6rem;
  font-size: 0.8rem;
  color: $pp-text-muted;
  background: $pp-surface;
  border: 1px solid $pp-border-strong;
  border-radius: $pp-radius-sm;

  &:focus {
    outline: none;
    border-color: $pp-brand;
    box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12);
  }
}

.waw-link__note {
  margin: 0.45rem 0 0;
  font-size: 0.78rem;
  line-height: 1.55;
  color: $pp-text-muted;
}

.waw-link__note--warn {
  color: $pp-red-text;
  font-weight: 600;
}

.waw-waiting {
  display: flex;
  align-items: center;
  margin: 0 0 0.9rem;
  color: $pp-text-muted;
  font-size: 0.85rem;
}

.waw-fineprint {
  margin: 0;
  color: $pp-text-subtle;
  font-size: 0.8rem;
  line-height: 1.55;
  max-width: 620px;
}
</style>
